import type { ReactNode } from 'react';
import { Cpu, Lock, ShieldCheck } from 'lucide-react';

/**
 * Nesting diagram — three concentric commitments.
 * Our stack runs inside the customer's environment, under the customer's terms.
 * Reads outside-in on desktop, top-down on mobile.
 */

const layers = [
  {
    icon: ShieldCheck,
    title: 'Your Data, Your Rules',
    detail: 'GDPR compliant on every plan. Never used for training, never sold, deleted on request.',
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/[0.04]',
    iconColor: 'text-amber-400',
    tag: 'The terms everything runs under',
  },
  {
    icon: Lock,
    title: 'Your Environment',
    detail: 'Managed by DialogMe, or entirely inside your own network as a Fnk deployment. You choose.',
    border: 'border-cyan-500/30',
    bg: 'bg-cyan-500/[0.05]',
    iconColor: 'text-cyan-400',
    tag: 'Where it runs is your decision',
  },
  {
    icon: Cpu,
    title: 'The Fnk AI Stack',
    detail: 'Our own AI stack — inference we run ourselves rather than reselling someone else’s API.',
    border: 'border-indigo-500/40',
    bg: 'bg-indigo-500/[0.07]',
    iconColor: 'text-indigo-400',
    tag: 'The part we control end to end',
  },
];

export function StackLayers() {
  return (
    <div className="max-w-3xl mx-auto">
      {layers.reduceRight<ReactNode>((inner, layer) => {
        const Icon = layer.icon;
        const isInnermost = inner === null;
        return (
          <div
            key={layer.title}
            className={`rounded-2xl border ${layer.border} ${layer.bg} p-4 sm:p-6`}
          >
            <div className="flex items-start gap-3 mb-3">
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${layer.iconColor}`} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h3 className="text-base font-semibold text-slate-100 tracking-tight">
                    {layer.title}
                  </h3>
                  <span className="text-[11px] text-slate-500">{layer.tag}</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mt-1">{layer.detail}</p>
              </div>
            </div>
            {!isInnermost && <div className="mt-4">{inner}</div>}
          </div>
        );
      }, null)}
    </div>
  );
}
